import CollapseIcon from "@mui/icons-material/ExpandLess";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  ButtonBase,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../../context/CaseContext";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import {
  ensureApportionmentItems,
  getCoveredQuantums,
} from "../../../shared/apportionmentEngine";
import { Stage } from "../../../shared/CaseConfiguration";
import {
  NamedEntity,
  OfferStatus,
  SettlementData,
  SettlementOffer,
} from "../../../shared/CaseModel";
import { getQuantums } from "../../../shared/CaseModelUtility";
import { damageName } from "../../../shared/damages";
import { judgmentOf } from "../../../shared/judgment";
import randomId from "../../../shared/randomId";
import { NO_SETTLEMENT_BASIS } from "../../../shared/settlementCaps";
import {
  chancesOf,
  SettlementChances,
  stageOfOffer,
  withChance,
  withPosition,
} from "../../../shared/settlementProspects";
import { currentStageOf } from "../../../shared/timeline";
import CenteredMessageBox from "../../general/CenteredMessageBox";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SectionLabel } from "../../general/SectionLabel";
import { Tabs } from "../../general/Tabs";
import { TonalBadge } from "../../general/TonalBadge";
import { byName, clientsFirst, sort } from "../AnalyticsUtility";
import { SettlementChart } from "./SettlementChart";
import { GuideHeader } from "../../guide/GuideHeader";
import { AdjustedValue } from "./AdjustedValue";
import { asChartData, OfferPoint, rangeColors } from "./SettlementChartData";
import { Offer, SideBasis } from "./SettlementOfferDialog";
import SettlementOffers from "./SettlementOffers";
import SettlementSummary, { useStanding } from "./SettlementSummary";
import {
  isSettled,
  OpponentSettlements,
  signedAmount,
} from "./settlementModel";

/** How strongly the band over an opponent's section is washed with the colour of its standing. */
const BAND_ALPHA = 0.1;

export const SettlementAnalytics = () => {
  const caseContext = useCase();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const parties = caseContext.getPartyDescriptors();
  const submit = useCaseSubmit();
  const saved = caseContext.getCase().settlementChances;

  // what was just entered counts at once, while the save calculates the case
  const [chances, setChances] = useState(saved);
  useEffect(() => setChances(saved), [saved]);
  const changeChances = (changed: SettlementChances[]) => {
    setChances(changed);
    return submit(
      { settlementChances: JSON.stringify(changed) },
      { method: "PUT" },
    );
  };

  const sortedParties: string[] = useMemo(
    () => sort(parties, clientsFirst, byName),
    [parties],
  );

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] =
    sortedParties.map((partyId) => ({
      label: (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <span>{parties[partyId].name}</span>
          {parties[partyId].isClient && (
            <TonalBadge tone={theme.palette.primary}>
              {t("parties.clientBadge")}
            </TonalBadge>
          )}
        </Stack>
      ),
      tabComponent: (
        <SettlementsOfParty
          party={{ id: partyId, name: parties[partyId].name }}
          opponentIds={sortedParties.filter((id) => id !== partyId)}
          chances={chances}
          onChange={changeChances}
        />
      ),
    }));

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("analytics.settlement.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
          >
            {t("analytics.settlement.intro")}
          </Typography>
        </Box>
      </GuideHeader>

      <Box>
        <Tabs tabs={tabs} />
      </Box>
    </Stack>
  );
};

/** Where a new offer between two parties stands, and what the costs of each side offer it. */
type NewOffer = Pick<Offer, "order" | "bases">;

/** The settlements of one party: where it stands, and its offers opponent by opponent. */
const SettlementsOfParty = ({
  party,
  opponentIds,
  chances,
  onChange,
}: {
  party: NamedEntity;
  /** The other parties of the case, in the order of the page. */
  opponentIds: string[];
  chances: SettlementChances[] | undefined;
  onChange: (changed: SettlementChances[]) => void;
}) => {
  const caseContext = useCase();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const c = caseContext.getCase();
  const parties = caseContext.getPartyDescriptors();
  const claimsAgainst = caseContext.getClaimMatrix().getOpponentsOf(party.id);
  const stages = caseContext.getStages();
  // once a settlement ended the case, offers stay at its stage, and once
  // every stage has ended, at the last
  const current = currentStageOf(c.timeline, stages);

  // each pair with what a new offer between the two starts from
  const pairs: (OpponentSettlements & { newOffer: NewOffer })[] = useMemo(
    () =>
      opponentIds
        .filter((opponentId) => claimsAgainst[opponentId] !== undefined)
        .map((opponentId) => {
          const opponent = { id: opponentId, name: parties[opponentId].name };
          // the claims between the two, as the page of the claims numbers them
          const numbered = (c.claims ?? [])
            .map((claim, index) => ({ claim, number: index + 1 }))
            .filter(({ claim }) =>
              claimsAgainst[opponentId].includes(claim.id),
            );
          const claimsOfCase = numbered.map(({ claim }) => claim);
          const allQuantums = getQuantums(claimsOfCase);

          // what a side's spent costs offer its settlements, as the
          // calculation has them (see settlementCaps)
          const sideOf = (partyId: string): SideBasis => ({
            basis: c.results?.settlementBases?.[partyId] ?? NO_SETTLEMENT_BASIS,
            vatRate: c.parties?.find(({ id }) => id === partyId)?.vatRate,
          });

          const offers: Offer[] = (c.settlements ?? [])
            .map((settlement, order) => ({ settlement, order }))
            // consider settlements only between the two parties
            .filter(
              ({ settlement: { offerorId, offereeId } }) =>
                (offerorId === party.id && offereeId === opponentId) ||
                (offereeId === party.id && offerorId === opponentId),
            )
            .map(({ settlement, order }) => {
              const settledQuantumIds: string[] = (
                settlement.settledQuantums ?? []
              ).map((q: string | { id: string }) =>
                typeof q === "string" ? q : q.id,
              );
              return {
                id: settlement.id,
                offeror: {
                  id: settlement.offerorId,
                  name:
                    parties[settlement.offerorId].name ?? settlement.offerorId,
                },
                offeree: {
                  id: settlement.offereeId,
                  name:
                    parties[settlement.offereeId].name ?? settlement.offereeId,
                },
                payment: settlement.payment,
                stage: stageOfOffer(settlement, current),
                status: settlement.status,
                ...(settlement.acceptedOn && {
                  acceptedOn: settlement.acceptedOn,
                }),
                claims: numbered.map(({ claim, number }) => ({
                  id: claim.id,
                  number,
                  title: claim.title,
                  settledByOffer: settlement.settledClaims.includes(claim.id),
                  quantumIds: (claim.quantums ?? []).map((q) => q.damageId),
                })),
                // each damage of the claims once, at its highest amount
                quantums: allQuantums.map((quantum) => ({
                  id: quantum.damageId,
                  title: damageName(c.damages, quantum.damageId),
                  value: quantum.value,
                })),
                // sync apportionment items with current settled quantums
                apportionment: ensureApportionmentItems(
                  settlement.apportionment ?? [],
                  settledQuantumIds,
                ),
                costs: { settlement: settlement.costs.settlement },
                order,
                bases: {
                  offeror: sideOf(settlement.offerorId),
                  offeree: sideOf(settlement.offereeId),
                },
              };
            });

          return {
            opponent,
            // made by the party, after every offer of the case
            newOffer: {
              order: (c.settlements ?? []).length,
              bases: { offeror: sideOf(party.id), offeree: sideOf(opponentId) },
            },
            claims: numbered.map(({ claim, number }) => ({
              id: claim.id,
              number,
              title: claim.title,
              amounts: (claim.quantums ?? []).map((q) => ({
                id: q.damageId,
                title: damageName(c.damages, q.damageId),
                value: q.value,
              })),
            })),
            offers,
          };
        }),
    [c, parties, claimsAgainst, opponentIds, party.id, current],
  );

  if (pairs.length === 0)
    return (
      <CenteredMessageBox message={t("analytics.settlement.noOpponents")} />
    );

  const settlementDataOfParty = caseContext
    .getSettlementData()
    .find((data) => data.partyId === party.id);

  return (
    // the sections of the opponents stand further apart than cards do
    <Stack spacing={3.5}>
      <SettlementSummary partyName={party.name} pairs={pairs} />
      <AdjustedValue party={party} current={current} />
      {pairs.map((pair) => (
        <SettlementsWithOpponent
          key={pair.opponent.id}
          party={party}
          pair={pair}
          newOffer={pair.newOffer}
          current={current}
          sharedWith={(offer) => {
            const covered = new Set(
              offer.claims
                .filter(({ settledByOffer }) => settledByOffer)
                .flatMap(({ quantumIds }) => quantumIds),
            );
            return pairs.find(
              (other) =>
                other !== pair &&
                !isSettled(other) &&
                other.claims.some(({ amounts }) =>
                  amounts.some(({ id }) => covered.has(id)),
                ),
            )?.opponent.name;
          }}
          chances={chancesOf(chances, party.id, pair.opponent.id)}
          onChanceChange={(stage, chance) =>
            onChange(
              withChance(chances, party.id, pair.opponent.id, stage, chance),
            )
          }
          onPositionChange={(position) =>
            onChange(
              withPosition(chances, party.id, pair.opponent.id, position),
            )
          }
          settlementData={settlementDataOfParty?.data.find(
            (data) => data.opponentId === pair.opponent.id,
          )}
        />
      ))}
    </Stack>
  );
};

/**
 * The offers between a party and one opponent as a timeline, and under it
 * the range the two could agree in with the offers in it.
 */
const SettlementsWithOpponent = ({
  party,
  pair,
  newOffer,
  settlementData,
  current,
  sharedWith,
  chances,
  onChanceChange,
  onPositionChange,
}: {
  party: NamedEntity;
  pair: OpponentSettlements;
  /** Where a new offer between the two stands, and what the costs of each side offer it. */
  newOffer: NewOffer;
  settlementData?: SettlementData;
  current: Stage;
  sharedWith: (offer: Offer) => string | undefined;
  chances: SettlementChances | undefined;
  onChanceChange: (stage: Stage, chance: number) => void;
  onPositionChange: (position: number) => void;
}) => {
  const caseContext = useCase();
  const { readOnly } = caseContext;
  const submit = useCaseSubmit();
  const standingOf = useStanding();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });

  const { opponent, claims, offers } = pair;
  const { badge, tone } = standingOf(pair);
  // the band over the section takes the colour of the standing, and stays
  // quiet where nothing is pending or decided
  const edge = tone?.main ?? theme.palette.text.disabled;
  const wash = tone ? alpha(tone.main, BAND_ALPHA) : theme.palette.action.hover;

  return (
    // a frame of its own for each opponent, under a band that names the two
    // parties: the offers stay cards of their own on the page's colour inside
    <Paper
      component="section"
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "background.default",
        borderTop: `4px solid ${edge}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          // the wash lies on the colour of a card, not on that of the page
          bgcolor: "background.paper",
          backgroundImage: `linear-gradient(${wash}, ${wash})`,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t("analytics.settlement.titleSettlementChart", {
            name: party.name,
            other: opponent.name,
          })}
        </Typography>
        <TonalBadge tone={tone} sx={{ bgcolor: "background.paper" }}>
          {badge}
        </TonalBadge>
        <Typography
          variant="caption"
          sx={{ ml: "auto", fontWeight: 500, color: "text.secondary" }}
        >
          {t("analytics.settlement.offers", { count: offers.length })}
        </Typography>
      </Box>

      <Box sx={{ px: 2.5, pt: 1.5, pb: 2.5 }}>
        <SettlementOffers
          party={party}
          opponent={opponent}
          create={() => ({
            id: randomId(),
            status: OfferStatus.Draft,
            stage: current,
            claims: claims.map(({ id, number, title, amounts }) => ({
              id,
              number,
              title,
              quantumIds: amounts.map((amount) => amount.id),
              settledByOffer: false,
            })),
            offeror: party,
            offeree: opponent,
            quantums: claims
              .flatMap(({ amounts }) => amounts)
              // a claimed amount of several claims is listed once
              .filter(
                (amount, index, all) =>
                  all.findIndex(({ id }) => id === amount.id) === index,
              ),
            apportionment: ensureApportionmentItems([], []),
            costs: { settlement: { offeror: {}, offeree: {} } },
            ...newOffer,
            payment: { amount: 0, offereePays: true },
          })}
          offers={offers}
          remove={async (offerId) =>
            await submit({ offerId }, { method: "DELETE" })
          }
          save={async (offer) => {
            const method = offers.some(({ id }) => id === offer.id)
              ? // update
                "PUT"
              : // add
                "POST";
            await submit({ offer: JSON.stringify(convert(offer)) }, { method });
          }}
          stages={caseContext.getStages()}
          readOnly={readOnly}
          settled={isSettled(pair)}
          current={current}
          sharedWith={sharedWith}
          judged={judgmentOf(caseContext.getCase()) !== undefined}
          chances={chances}
          onChanceChange={onChanceChange}
          onPositionChange={onPositionChange}
        />

        {settlementData && (
          <BargainingRangeCard
            party={party}
            opponent={opponent}
            settlementData={settlementData}
            offers={offers.map((offer, index) => ({
              number: index + 1,
              stage: offer.stage,
              amount: signedAmount(offer, party.id),
              status: offer.status,
            }))}
          />
        )}
      </Box>
    </Paper>
  );
};

/**
 * The range the two sides could agree in by stage, with their offers in it,
 * and over it the values of the stage that is selected.
 */
const BargainingRangeCard = ({
  party,
  opponent,
  settlementData,
  offers,
}: {
  party: NamedEntity;
  opponent: NamedEntity;
  settlementData: SettlementData;
  offers: OfferPoint[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement",
  });
  // open where there are offers to see against the range
  const [open, setOpen] = useState(offers.length > 0);
  // the stage of the latest offer to begin with, the first one without offers
  const [stageIndex, setStageIndex] = useState(() => {
    const latest = offers[offers.length - 1];
    const index = settlementData.perStage.findIndex(
      ({ stage }) => stage === latest?.stage,
    );
    return Math.max(0, index);
  });

  const { chart, marks } = useMemo(
    () => asChartData(settlementData, party.name, opponent.name, theme, offers),
    [settlementData, party.name, opponent.name, theme, offers],
  );

  const selected = settlementData.perStage[stageIndex];
  const colors = rangeColors(theme);
  const values = [
    {
      label: t("range.caseValue", { name: party.name }),
      value: selected?.range.partyEv ?? 0,
      line: `2.5px solid ${colors.party}`,
    },
    {
      label: t("range.inverseCaseValue", { name: opponent.name }),
      value: selected?.range.opponentEv ?? 0,
      line: `2.5px solid ${colors.opponent}`,
    },
    {
      label: t("chart.labels.midpoint"),
      value: selected?.range.mid ?? 0,
      line: `2.5px dashed ${colors.midpoint}`,
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, mt: 1.5 }}>
      <ButtonBase
        aria-expanded={open}
        aria-label={open ? t("range.hide") : t("range.show")}
        onClick={() => setOpen(!open)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 2.5,
          py: 1.625,
          borderRadius: 3,
          textAlign: "left",
        }}
      >
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
          {t("range.title")}
        </Typography>
        {open ? (
          <CollapseIcon sx={{ color: "text.secondary" }} />
        ) : (
          <ExpandIcon sx={{ color: "text.secondary" }} />
        )}
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2.25 }}>
          <SectionLabel component="h4">
            {t("range.valuesAt", {
              stage: selected
                ? t(Stage[selected.stage], { keyPrefix: "case.stages" })
                : "",
            })}
          </SectionLabel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              columnGap: 3.25,
              rowGap: 1.75,
              mt: 1.25,
              mb: 2,
            }}
          >
            {values.map(({ label, value, line }) => (
              <div key={label}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.875,
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ flex: "none", width: 16, borderTop: line }}
                  />
                  {label}
                </Typography>
                <Typography
                  variant="h5"
                  component="p"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-.015em",
                    ...TABULAR_NUMERALS,
                  }}
                >
                  {currency.format(value)}
                </Typography>
              </div>
            ))}
          </Box>
          <SettlementChart
            chartData={chart}
            marks={marks}
            index={stageIndex}
            onIndexSelect={setStageIndex}
          />
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.5, color: "text.secondary" }}
          >
            {t("range.caption")}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export function convert(offer: Offer): SettlementOffer {
  return {
    id: offer.id,
    offerorId: offer.offeror.id,
    offereeId: offer.offeree.id,
    costs: {
      settlement: offer.costs.settlement,
    },
    payment: offer.payment,
    stage: offer.stage,
    status: offer.status,
    // the day of acceptance stays with an accepted offer only
    ...(offer.status === OfferStatus.Accepted &&
      offer.acceptedOn && { acceptedOn: offer.acceptedOn }),
    settledClaims: offer.claims
      .filter(({ settledByOffer }) => settledByOffer)
      .map(({ id }) => id),
    settledQuantums: getCoveredQuantums(offer.claims, offer.quantums).map(
      ({ id }) => id,
    ),
    apportionment: offer.apportionment,
  };
}
